import { useEffect, useRef, useState } from "react";
import { applyAssistantTurn, assistantIntroReply, assistantOpeningReply, assistantVisibleReply, hasCompleteGoalProfile, initialAssistantState, loadAssistantState, requestAssistantTurn, resetAssistantState, restartAssistantDataCollection, saveAssistantState, type AssistantState } from "../../application/assistant-prototype";
import { detectMealType } from "../../application/meal-detection";
import { parseVoiceMealRemotely, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";
import { mergeVoiceTranscript } from "../../application/voice-transcript";
import type { VoiceTranscriptionSession } from "../../application/voice-transcription";
import { voiceErrorText } from "../voice-input/voice-errors";
import { primeTypewriterSound } from "../../ui/components/TypewriterText";

type AssistantView = "closed" | "goal" | "persona-choice" | "intro" | "ready" | "starting" | "listening" | "processing" | "summary" | "error";

export function useAssistant({ previewAssistantState = null, voiceProvider, presentParsedDraft }: { previewAssistantState?: AssistantState | null; voiceProvider: import("../../application/voice-transcription").VoiceTranscriptionProvider; presentParsedDraft: (draft: ParsedVoiceMealDraft) => void }) {
  const [assistantState, setAssistantState] = useState<AssistantState>(() => previewAssistantState ?? (typeof localStorage === "undefined" ? initialAssistantState() : loadAssistantState()));
  const [assistantView, setAssistantView] = useState<AssistantView>(() => previewAssistantState ? "summary" : "closed");
  const [assistantReply, setAssistantReply] = useState("");
  const [assistantIntroTyped, setAssistantIntroTyped] = useState(false);
  const [assistantIntroActionReady, setAssistantIntroActionReady] = useState(false);
  const [assistantTranscript, setAssistantTranscript] = useState("");
  const [assistantInfoOpen, setAssistantInfoOpen] = useState(false);
  const assistantSession = useRef<VoiceTranscriptionSession | null>(null);
  const assistantIntroActionTimer = useRef<number | null>(null);
  const assistantFinalText = useRef("");
  const assistantCurrentText = useRef("");
  const openAssistant = () => {
    primeTypewriterSound();
    const stored = loadAssistantState();
    const restarted = restartAssistantDataCollection(stored);
    saveAssistantState(restarted);
    setAssistantState(restarted);
    setAssistantTranscript("");
    if (!restarted.personaGender) {
      setAssistantView("persona-choice");
      return;
    }
    setAssistantReply(assistantIntroReply(restarted));
    setAssistantIntroTyped(false);
    setAssistantIntroActionReady(false);
    setAssistantView("intro");
  };

  const choosePersonaGender = (choice: "female" | "male" | "surprise") => {
    const personaGender = choice === "surprise" ? (Math.random() < 0.5 ? "female" : "male") : choice;
    const next: AssistantState = restartAssistantDataCollection({ ...assistantState, personaGender, profile: { ...assistantState.profile, personaGender } });
    saveAssistantState(next);
    setAssistantState(next);
    setAssistantReply(assistantIntroReply(next));
    setAssistantIntroTyped(false);
    setAssistantIntroActionReady(false);
    setAssistantView("intro");
  };

  const completeAssistantIntro = () => {
    setAssistantIntroTyped(true);
    if (assistantIntroActionTimer.current !== null) window.clearTimeout(assistantIntroActionTimer.current);
    assistantIntroActionTimer.current = window.setTimeout(() => {
      setAssistantIntroActionReady(true);
      assistantIntroActionTimer.current = null;
    }, 650);
  };

  const startAssistantGoal = () => {
    if (assistantIntroActionTimer.current !== null) window.clearTimeout(assistantIntroActionTimer.current);
    primeTypewriterSound();
    setAssistantReply(assistantOpeningReply(assistantState));
    setAssistantView("ready");
  };

  const closeAssistant = () => {
    if (assistantIntroActionTimer.current !== null) window.clearTimeout(assistantIntroActionTimer.current);
    assistantSession.current?.abort();
    assistantSession.current = null;
    window.speechSynthesis?.cancel();
    setAssistantView("closed");
  };

  const submitAssistantAnswer = async (text: string) => {
    if (!text.trim()) return;
    setAssistantView("processing");
    try {
      const turn = await requestAssistantTurn(assistantState, text.trim());
      const applied = applyAssistantTurn(assistantState, text.trim(), turn);
      const next = assistantState.stage === "metrics" && hasCompleteGoalProfile(applied.profile) ? { ...applied, stage: "complete" as const } : applied;
      saveAssistantState(next);
      setAssistantState(next);
      setAssistantReply(assistantVisibleReply(assistantState.stage, next, turn.reply));
      setAssistantTranscript("");
      if (turn.mealIntent === "confirmed" && turn.mealText) {
        const mealType = detectMealType(turn.mealText);
        if (mealType) {
          try { presentParsedDraft(await parseVoiceMealRemotely(turn.mealText, mealType)); } catch { /* The conversation remains available if meal parsing fails. */ }
        }
      }
      setAssistantView(next.stage === "complete" && assistantState.stage !== "complete" ? "summary" : "ready");
    } catch {
      setAssistantReply("Связь с ЯмНямом потерялась. Попробуем ещё раз?");
      setAssistantView("error");
    }
  };

  const startAssistantListening = () => {
    assistantFinalText.current = "";
    assistantCurrentText.current = "";
    setAssistantTranscript("");
    setAssistantView("starting");
    assistantSession.current = voiceProvider.start("ru-RU", {
      onListening: () => setAssistantView("listening"),
      onCaptureEnd: () => setAssistantView("processing"),
      onInterim: text => {
        const combined = mergeVoiceTranscript(assistantFinalText.current, text);
        assistantCurrentText.current = combined;
        setAssistantTranscript(combined);
      },
      onFinal: text => {
        const combined = mergeVoiceTranscript(assistantFinalText.current, text);
        assistantCurrentText.current = combined;
        assistantFinalText.current = combined;
        setAssistantTranscript(combined);
      },
      onError: error => { setAssistantReply(voiceErrorText[error]); setAssistantView("error"); },
      onEnd: () => {
        const recognized = (assistantFinalText.current || assistantCurrentText.current).trim();
        assistantSession.current = null;
        if (recognized) void submitAssistantAnswer(recognized);
        else {
          setAssistantReply("Я пока ничего не расслышал. Попробуй ещё раз.");
          setAssistantView("ready");
        }
      },
    });
  };

  const toggleAssistantMicrophone = () => {
    if (assistantView !== "listening") {
      startAssistantListening();
      return;
    }
    // stop() asks the speech recognizer to finalize the current audio. Its final
    // result arrives through onFinal/onEnd; the visible interim text is only a
    // fallback for engines that do not emit a separate final event.
    setAssistantView("processing");
    assistantSession.current?.stop();
  };

  const restartAssistant = () => {
    const next = resetAssistantState();
    setAssistantState(next);
    setAssistantInfoOpen(false);
    setAssistantView("persona-choice");
  };

  const restartGoalCollection = () => {
    const next = restartAssistantDataCollection(assistantState);
    saveAssistantState(next);
    setAssistantState(next);
    setAssistantReply(assistantOpeningReply(next));
    setAssistantTranscript("");
    setAssistantView("ready");
  };

  useEffect(() => () => {
    if (assistantIntroActionTimer.current !== null) window.clearTimeout(assistantIntroActionTimer.current);
    assistantSession.current?.abort();
  }, []);

  return { assistantState, assistantView, assistantReply, assistantIntroTyped, assistantIntroActionReady, assistantTranscript, assistantInfoOpen, setAssistantInfoOpen, openAssistant, choosePersonaGender, completeAssistantIntro, startAssistantGoal, closeAssistant, toggleAssistantMicrophone, restartAssistant, restartGoalCollection };
}
